import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allArticles, type ArticleData } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hipobuyspreadsheet.pro';

function buildArticleJsonLd(article: ArticleData, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.subtitle,
    url,
    datePublished: '2026-06-23',
    dateModified: '2026-06-23',
    author: {
      '@type': 'Organization',
      name: 'Hipobuy Spreadsheet',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Hipobuy Spreadsheet',
      url: SITE_URL,
    },
    inLanguage: 'fr-FR',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  };
}

function buildBreadcrumbJsonLd(article: ArticleData, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Accueil',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: url,
      },
    ],
  };
}

function ArticleContent({ article }: { article: ArticleData }) {
  const articleSlugs = allArticles.map(a => a.slug);
  const otherArticles = allArticles.filter(a => a.slug !== article.slug);

  const internalArticleLinks = otherArticles.slice(0, 2);
  const extraArticleLink = otherArticles.length > 2 ? otherArticles[2] : otherArticles[0];

  return (
    <>
      <section className="relative w-full pt-24 pb-14 md:pt-32 md:pb-20 bg-background-100 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <img
            src={article.imageUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">
              {article.category}
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl leading-relaxed mb-6">
              {article.subtitle}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Juin 2026
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime} de lecture
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-bookmark-line"></i>
                {article.category}
              </span>
            </div>
          </div>
        </div>
      </section>

      <article className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="space-y-10">
              {article.sections.map((section, sIdx) => (
                <section key={sIdx}>
                  <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-4">
                    {section.heading}
                  </h2>
                  <div className="space-y-4">
                    {section.content.map((block, bIdx) => {
                      if (block.type === 'paragraph') {
                        return (
                          <p
                            key={bIdx}
                            className="text-sm md:text-base text-foreground-600 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: block.text }}
                          />
                        );
                      }
                      if (block.type === 'highlight') {
                        return (
                          <div key={bIdx} className="bg-accent-50 rounded-xl p-5 border border-accent-200/60 my-6">
                            <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
                              <i className="ri-lightbulb-line text-accent-600 mr-2"></i>
                              À retenir
                            </h4>
                            <p className="text-sm text-foreground-600">{block.text}</p>
                          </div>
                        );
                      }
                      if (block.type === 'list') {
                        return (
                          <ul key={bIdx} className="space-y-3 list-none">
                            {block.items.map((item, iIdx) => (
                              <li key={iIdx} className="flex items-start gap-3 text-sm text-foreground-600">
                                <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-accent-600 flex-shrink-0">
                                  <i className="ri-check-line"></i>
                                </span>
                                <span dangerouslySetInnerHTML={{ __html: item }} />
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      return null;
                    })}
                  </div>
                </section>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-4">
                Articles similaires
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {internalArticleLinks.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-article-line"></i>
                    </span>
                    <span className="text-sm text-foreground-700 font-medium">{a.title}</span>
                  </Link>
                ))}
              </div>
              <div className="mt-4 text-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Retour à l&apos;accueil Hipobuy Spreadsheet
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-4">
              Continuer vos achats
            </h2>
            <p className="text-sm md:text-base text-foreground-500 mb-8">
              Retrouvez toutes les catégories mode sur notre boutique principale
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                Buy it Now
                <i className="ri-arrow-right-line"></i>
              </a>
              <Link
                to="/"
                className="w-full sm:w-auto px-8 py-3.5 rounded-full border-2 border-foreground-200 text-foreground-700 text-sm md:text-base font-medium hover:border-primary-400 hover:text-primary-700 transition-all whitespace-nowrap cursor-pointer"
              >
                ← Retour à l&apos;accueil
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="w-full px-4 md:px-6 text-center">
            <h1 className="font-heading font-bold text-2xl text-foreground-900 mb-4">Article non trouvé</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">
              Retour à l&apos;accueil
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleUrl = `${SITE_URL}/article/${slug}`;

  return (
    <>
      <title>{article.title}</title>
      <meta name="description" content={article.subtitle} />
      <meta name="keywords" content={`hipobuy spreadsheet, ${article.category.toLowerCase()}, guide spreadsheet 2026, tutoriel spreadsheet`} />
      <link rel="canonical" href={articleUrl} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.subtitle} />
      <meta property="og:url" content={articleUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:locale" content="fr_FR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.subtitle} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildArticleJsonLd(article, articleUrl)) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildBreadcrumbJsonLd(article, articleUrl)) }}
      />

      <Navbar />
      <main>
        <ArticleContent article={article} />
      </main>
      <Footer />
    </>
  );
}